// src/lib/geofencesApi.js
// API-first (server-owned) - Geofences v1 ctx-org
// Blindaje: NUNCA enviar columnas computadas / generated (bbox, geom, audit)

import { supabase } from "./supabaseClient";

function getJsonHeaders(extra = {}) {
  return {
    "Content-Type": "application/json",
    "X-Api-Version": "geofences-api-v1-ctx-org-server-owned",
    ...extra,
  };
}

function safeJsonParse(text) {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

async function getAccessToken() {
  const {
    data: { session },
    error,
  } = await supabase.auth.getSession();

  if (error) {
    const err = new Error(error.message || "session_error");
    err.status = 401;
    throw err;
  }

  const accessToken = session?.access_token || null;

  if (!accessToken) {
    const err = new Error("missing_token");
    err.status = 401;
    throw err;
  }

  return accessToken;
}

async function requestJson(url, { method = "GET", body = null, headers = {} } = {}) {
  const accessToken = await getAccessToken();

  const res = await fetch(url, {
    method,
    headers: getJsonHeaders({
      Authorization: `Bearer ${accessToken}`,
      ...headers,
    }),
    credentials: "include",
    body: body ? JSON.stringify(body) : null,
  });

  const txt = await res.text();
  const data = txt ? safeJsonParse(txt) : null;

  if (!res.ok || (data && data.ok === false)) {
    const err = new Error(data?.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.payload = data || { ok: false, error: `HTTP ${res.status}`, details: txt };
    throw err;
  }

  return data;
}

const STRIP_FIELDS = new Set([
  // generated/computed
  "bbox",
  "geom",
  // audit
  "created_at",
  "updated_at",
  "deleted_at",
  "revoked_at",
  "created_by",
  "updated_by",
  // tenancy/ownership (server ctx)
  "org_id",
  "tenant_id",
  "user_id",
  "owner_id",
  "usuario_id",
  // bridge (server-owned)
  "source_geocerca_id",
]);

function stripComputedFields(obj) {
  if (!obj || typeof obj !== "object") return obj;
  const out = Array.isArray(obj) ? [...obj] : { ...obj };
  for (const k of Object.keys(out)) {
    if (STRIP_FIELDS.has(k)) delete out[k];
  }
  return out;
}

// Nueva versión solo frontend: usa API
export async function listGeofences(orgId, onlyActive) {
  const params = new URLSearchParams();
  params.set("action", "list");
  if (orgId) params.set("orgId", String(orgId));
  if (onlyActive) params.set("onlyActive", "true");
  const data = await requestJson(`/api/geofences?${params.toString()}`, { method: "GET" });
  return data?.items || [];
}

export async function getGeofence({ id, orgId = null } = {}) {
  if (!id) throw new Error("id requerido");
  const params = new URLSearchParams();
  params.set("action", "get");
  params.set("id", String(id));
  if (orgId) params.set("orgId", String(orgId));
  const data = await requestJson(`/api/geofences?${params.toString()}`, { method: "GET" });
  return data?.item || null;
}

export async function upsertGeofence(payload = {}) {
  const orgId = payload?.org_id ?? payload?.orgId;
  if (orgId === undefined || orgId === null || String(orgId).trim() === "") {
    throw new Error("orgId requerido");
  }

  const clean = stripComputedFields(payload);
  const data = await requestJson(`/api/geofences`, {
    method: "POST",
    headers: { "x-org-id": String(orgId) },
    body: { action: "upsert", ...clean },
  });
  if (data?.item) return data.item;
  if (Array.isArray(data?.items) && data.items.length) return data.items[0];
  return null;
}

export async function deleteGeofence({ orgId = null, id = null } = {}) {
  if (!id) throw new Error("deleteGeofence: requiere id");
  const payload = { action: "delete", id: String(id) };
  if (orgId) payload.orgId = String(orgId);
  const data = await requestJson(`/api/geofences`, {
    method: "POST",
    headers: orgId ? { "x-org-id": String(orgId) } : {},
    body: payload,
  });
  return {
    ok: data?.ok === true,
    mode: data?.mode || null,
    reason: data?.reason || null,
  };
}

// (Eliminado helper geofencesHasTenantId: no debe existir en frontend)